import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { I18nextProvider } from 'react-i18next'
import i18n, { SUPPORTED_LOCALES } from '@/i18n'
import { LocaleProvider } from '@/i18n/LocaleProvider'
import { ThemeProvider } from '@/theme/ThemeProvider'
import { ConsentProvider } from '@/analytics/ConsentProvider'
import { CopyContentProvider } from '@/content/copy/CopyContentProvider'
import { Layout } from '@/components/Layout'
import Home from '@/routes/Home'
import NewsIndex from '@/routes/NewsIndex'
import NewsPost from '@/routes/NewsPost'
import Privacy from '@/routes/Privacy'
import Terms from '@/routes/Terms'
import NotFound from '@/routes/NotFound'

// Dev-only variant overlay. The dynamic import sits inside a constant-folded
// `import.meta.env.DEV` branch, so Vite drops the whole chunk (including the
// variant-*.copy files it imports) from production builds.
const CopyVariantOverlay = import.meta.env.DEV
  ? lazy(() => import('@/content/copy/CopyVariantOverlay'))
  : null

function LocalizedRoutes() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="news" element={<NewsIndex />} />
        <Route path="news/:slug" element={<NewsPost />} />
        <Route path="privacy" element={<Privacy />} />
        <Route path="terms" element={<Terms />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  )
}

export default function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <CopyContentProvider>
        <ThemeProvider>
          <ConsentProvider>
            <BrowserRouter>
              <LocaleProvider>
                <Routes>
                  <Route path="/*" element={<LocalizedRoutes />} />
                  {SUPPORTED_LOCALES.filter((l) => l !== 'en').map((l) => (
                    <Route key={l} path={`/${l}/*`} element={<LocalizedRoutes />} />
                  ))}
                </Routes>
              </LocaleProvider>
            </BrowserRouter>
            {CopyVariantOverlay && (
              <Suspense fallback={null}>
                <CopyVariantOverlay />
              </Suspense>
            )}
          </ConsentProvider>
        </ThemeProvider>
      </CopyContentProvider>
    </I18nextProvider>
  )
}
